import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tag, Package, Settings, Grid, RefreshCw,
  MapPin, Users, Store, Star, Gift, LogOut, Menu, X, Banknote, LayoutDashboard
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import AdminCategories from '../components/admin/AdminCategories';
import AdminMenuItems from '../components/admin/AdminMenuItems';
import AdminOptionSets from '../components/admin/AdminOptionSets';
import AdminCoupons from '../components/admin/AdminCoupons';
import AdminDeliveryZones from '../components/admin/AdminDeliveryZones';
import AdminRestaurantSettings from '../components/admin/AdminRestaurantSettings';
import AdminOrders from '../components/admin/AdminOrders';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminReviews from '../components/admin/AdminReviews';
import AdminStaticPages from '../components/admin/AdminStaticPages';
import AdminRewards from '../components/admin/AdminRewards';
import AdminPromotionsManager from "../components/admin/AdminPromotionsManager";
import AdminPayouts from '../components/admin/AdminPayouts';
import AdminDashboard from '../components/admin/AdminDashboard';
import './AdminShell.css';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', description: 'Track sales, revenue and best sellers', icon: LayoutDashboard, section: 'Operations' },
  { id: 'orders', label: 'Orders', description: 'Review and manage incoming orders', icon: RefreshCw, section: 'Operations' },
  { id: 'customers', label: 'Customers', description: 'View customer activity and order history', icon: Users, section: 'Operations' },
  { id: 'reviews', label: 'Reviews', description: 'Read and moderate customer feedback', icon: Star, section: 'Operations' },
  { id: 'payouts', label: 'Payouts', description: 'View Stripe balances and scheduled payouts', icon: Banknote, section: 'Finance' },
  { id: 'categories', label: 'Categories', description: 'Organise the structure of your menu', icon: Grid, section: 'Menu' },
  { id: 'items', label: 'Menu Items', description: 'Create and update dishes, prices and availability', icon: Package, section: 'Menu' },
  { id: 'optionsets', label: 'Option Sets', description: 'Manage choices, extras and modifiers', icon: Tag, section: 'Menu' },
  { id: 'coupons', label: 'Coupons', description: 'Create discount codes and usage rules', icon: Tag, section: 'Marketing' },
  { id: 'promotions', label: 'Promotions', description: 'Build offers and reward-based promotions', icon: Gift, section: 'Marketing' },
  { id: 'rewards', label: 'Rewards', description: 'Configure your customer loyalty programme', icon: Gift, section: 'Marketing' },
  { id: 'zones', label: 'Delivery Zones', description: 'Set delivery areas, charges and minimum spends', icon: MapPin, section: 'Restaurant' },
  { id: 'pages', label: 'Static Pages', description: 'Edit your restaurant information pages', icon: Settings, section: 'Restaurant' },
  { id: 'settings', label: 'Settings', description: 'Manage restaurant details, opening hours and integrations', icon: Store, section: 'Restaurant' },
];

const TAB_SECTIONS = ['Operations', 'Finance', 'Menu', 'Marketing', 'Restaurant'];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAdmin, authLoading } = useApp();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [restaurant, setRestaurant] = useState(null);
  const [pauseOrdersSaving, setPauseOrdersSaving] = useState(false);
  const [pauseOrdersError, setPauseOrdersError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const activeTabMeta = TABS.find(tab => tab.id === activeTab) || TABS[0];
  const brandImage = restaurant?.logoUrl || restaurant?.faviconUrl;

  const logout = () => {
    localStorage.removeItem('mj_token');
    window.location.assign('/login');
  };

  const togglePauseIncomingOrders = async () => {
    if (!restaurant || pauseOrdersSaving) return;
    const paused = !Boolean(restaurant.pauseIncomingOrders);
    setPauseOrdersSaving(true);
    setPauseOrdersError('');
    try {
      const result = await api.setPauseIncomingOrders(paused);
      setRestaurant(current => ({
        ...current,
        pauseIncomingOrders: Boolean(result.pauseIncomingOrders)
      }));
    } catch (error) {
      setPauseOrdersError(error?.response?.data?.detail || 'Could not update ordering availability.');
    } finally {
      setPauseOrdersSaving(false);
    }
  };

useEffect(() => {
  let tag = document.querySelector('meta[name="robots"]');

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', 'noindex, nofollow');

  return () => {
    tag.setAttribute('content', 'index, follow');
  };
}, []);

  useEffect(() => {
    if (isAdmin) {
      api.getAdminOrderSummary()
        .then(summary => setPendingOrderCount(summary.pendingCount || 0))
        .catch(() => {});
    }
  }, [isAdmin]);

    useEffect(() => {
  api.getRestaurant().then(setRestaurant).catch(() => {});
  }, []);

  if (authLoading) {
    return (
      <div className="admin-access-screen">
        <div className="admin-access-card">
          <div className="admin-access-icon"><Store aria-hidden="true" /></div>
          <p className="admin-eyebrow">Restaurant management</p>
          <h2>Checking access</h2>
          <p>Please wait while your administrator session is verified.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-access-screen">
        <div className="admin-access-card">
          <div className="admin-access-icon"><Store aria-hidden="true" /></div>
          <p className="admin-eyebrow">Restaurant management</p>
          <h2>Admin access required</h2>
          <p>Sign in with an administrator account to manage this restaurant.</p>
          <button onClick={() => navigate('/login')} className="admin-primary-button">
            Sign In as Admin
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="admin-shell">
      {mobileMenuOpen && <button className="admin-mobile-menu-backdrop" aria-label="Close navigation" onClick={() => setMobileMenuOpen(false)} />}
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'is-mobile-open' : ''}`} aria-label="Admin navigation">
        <div className="admin-brand">
          <div className="admin-brand-mark">
            {brandImage ? <img src={brandImage} alt="" /> : <Store aria-hidden="true" />}
          </div>
          <div className="admin-brand-copy">
            <span>Orderkit</span>
            <strong>{restaurant?.name || 'Restaurant admin'}</strong>
          </div>
          <button className="admin-mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close navigation"><X /></button>
        </div>

        <nav className="admin-sidebar-nav">
          {TAB_SECTIONS.map(section => (
            <div className="admin-nav-section" key={section}>
              <p>{section}</p>
              {TABS.filter(tab => tab.section === section).map(tab => {
                const Icon = tab.icon;
                const isOrders = tab.id === 'orders';
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={activeTab === tab.id ? 'is-active' : ''}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                  >
                    <Icon aria-hidden="true" />
                    <span>{tab.label}</span>
                    {isOrders && pendingOrderCount > 0 && (
                      <span className="admin-count-badge">{pendingOrderCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className={`admin-pause-orders ${restaurant?.pauseIncomingOrders ? 'is-paused' : ''}`}>
          <div className="admin-pause-orders-copy">
            <strong>Pause incoming orders</strong>
            <span>{restaurant?.pauseIncomingOrders ? 'Orders are paused' : 'Accepting orders'}</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(restaurant?.pauseIncomingOrders)}
            aria-label="Pause incoming orders"
            disabled={!restaurant || pauseOrdersSaving}
            onClick={togglePauseIncomingOrders}
            className="admin-pause-orders-switch"
          >
            <span />
          </button>
          {pauseOrdersError && <p role="alert">{pauseOrdersError}</p>}
        </div>

        <div className="admin-sidebar-account">
          <div className="admin-user-card">
            <div><strong>{user?.name || 'Admin'}</strong><span>Administrator</span></div>
          </div>
        </div>
        <div className="admin-sidebar-footer">
          <img src="/Orderkit_BLK.svg" alt="Orderkit" />
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-inner">
            <div className="admin-page-heading">
              <h1>{activeTabMeta.label}</h1>
              <p>{activeTabMeta.description}</p>
            </div>
            <div className="admin-topbar-right">
              <div id="admin-page-actions" className="admin-page-actions" />
              <button type="button" className="admin-logout-button" onClick={logout}>
                <LogOut aria-hidden="true" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        <div className="admin-mobile-bar">
          <button type="button" className="admin-mobile-menu-button" onClick={() => setMobileMenuOpen(true)} aria-label="Open navigation"><Menu /></button>
          <div className="admin-mobile-brand-mark">{brandImage ? <img src={brandImage} alt="" /> : <Store />}</div>
          <div><strong>{restaurant?.name || 'Admin Panel'}</strong><span>{activeTabMeta.label}</span></div>
          {pendingOrderCount > 0 && <span className="admin-count-badge">{pendingOrderCount}</span>}
        </div>

        <div className="admin-content">
          {activeTab === 'dashboard' && <AdminDashboard />}
          {activeTab === 'categories' && <AdminCategories />}
          {activeTab === 'items' && <AdminMenuItems />}
          {activeTab === 'optionsets' && <AdminOptionSets />}
          {activeTab === 'coupons' && <AdminCoupons />}
          {activeTab === 'promotions' && <AdminPromotionsManager />}
          {activeTab === 'zones' && <AdminDeliveryZones />}
          {activeTab === 'settings' && <AdminRestaurantSettings />}
          {activeTab === 'pages' && <AdminStaticPages />}
          {activeTab === 'orders' && <AdminOrders onCountChange={setPendingOrderCount} />}
          {activeTab === 'customers' && <AdminCustomers />}
          {activeTab === 'rewards' && <AdminRewards />}
          {activeTab === 'reviews' && <AdminReviews />}
          {activeTab === 'payouts' && <AdminPayouts />}
        </div>
      </main>
    </div>
  );
}
